import type {
  ElementType,
  ReactElement,
  ReactNode,
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ForwardRefExoticComponent,
} from 'react';

// an object with unknown value
export type UnknownRecord = Record<string, unknown>;

// shortcut to get the props of an element/component
type PropsOf<Component extends ElementType> = ComponentPropsWithoutRef<Component>;

// given some base props and an override, merge them.
// prettier-ignore
type ExtendedProps<Props = UnknownRecord, OverrideProps = UnknownRecord> = Omit<Props, keyof OverrideProps> & OverrideProps;

// prop that allows overriding the rendered element
type ComponentProp<Component extends ElementType> = {
  /**
   * The component or HTML element to render as the root instead of the default.
   */
  renderAs?: Component;
};

/**
 * extend the polymorphic component props to always include children
 */
export type PolymorphicComponentProps<
  Component extends ElementType,
  Props = UnknownRecord,
> = ExtendedProps<PropsOf<Component>, ComponentProp<Component> & Props> & {
  children?: ReactNode;
  ref?: ComponentPropsWithRef<Component>['ref'];
};

/**
 * makePolymorphic takes a component (typically a forwardRef component) and returns a new version
 * that supports polymorphism via a `renderAs` prop. The returned component’s types automatically
 * merge the intrinsic props of the rendered element with the custom props of the original
 * component, while including support for children and refs.
 *
 * This allows for highly reusable UI components that can change the underlying HTML element or
 * component at the time of use—without sacrificing type safety or having to manually merge props
 * every time.
 */
export const makePolymorphic = <
  ComponentDefaultType extends ElementType, // eslint-disable-line @typescript-eslint/no-unnecessary-type-parameters -- needed for the polymorphic API
  Props,
  StaticComponents extends object = UnknownRecord, // eslint-disable-line @typescript-eslint/no-unnecessary-type-parameters -- needed for the polymorphic API
>(
  component: ForwardRefExoticComponent<unknown>,
) => {
  type PolymorphicComponent = <C extends ElementType = ComponentDefaultType>(
    props: PolymorphicComponentProps<C, Props>,
  ) => ReactElement | null;

  // cast via 'unknown' to convince TS that the conversion is intentional
  return component as unknown as PolymorphicComponent & StaticComponents;
};
